// Pure helpers for checking CASL-style ability rules returned by
// GET /access-control/me/ability. The backend keeps actions PascalCase on the
// wire (Read/Download/Manage/Access/TestMode) and treats `Manage` as the action
// wildcard and `all` as the subject wildcard. `action` may be a string or
// string[].
//
// Lifted from pages/AccessControl so any feature (menus, routes, components)
// can gate on DB-driven ability — not just the AccessControl admin screen.

import type {
  RbacAction,
  RbacSubject,
} from 'enrollment-platform/rbac/constants'

// A rule's subject may be the `all` wildcard, which is not one of the granular
// RbacSubjects, so it is widened here. `action` carries `Manage` (the wildcard)
// via RbacAction already.
export interface AbilityRule {
  action: RbacAction | RbacAction[]
  subject: RbacSubject | 'all'
}

// The action wildcard. Accept lowercase `manage` defensively too, in case the
// backend ever emits CASL's native lowercase form.
const MANAGE_WILDCARDS = ['Manage', 'manage']
// The subject wildcard.
const SUBJECT_WILDCARD = 'all'

function actionMatches(ruleAction: string | string[], action: string): boolean {
  const actions = Array.isArray(ruleAction) ? ruleAction : [ruleAction]
  return actions.some((a) => a === action || MANAGE_WILDCARDS.includes(a))
}

function subjectMatches(ruleSubject: string, subject: string): boolean {
  return ruleSubject === subject || ruleSubject === SUBJECT_WILDCARD
}

/**
 * True if the rule set grants `action` on `subject`, honoring the `Manage`
 * (action) and `all` (subject) wildcards. Equivalent to CASL's `ability.can`.
 */
export function can(
  rules: AbilityRule[] | undefined,
  action: RbacAction,
  subject: RbacSubject
): boolean {
  if (!rules) return false
  return rules.some(
    (rule) =>
      subjectMatches(rule.subject, subject) &&
      actionMatches(rule.action, action)
  )
}
